import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { isAnalyzableLoan, type AnalyzerLoan } from './analyzerLoans';

const log = createComponentLogger('useAnalyzerLoans', 'income-calculation');

export const ANALYZER_LOAN_PAGE_SIZE = 50;
export const ANALYZER_LOAN_SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

interface SearchResponse {
  loans?: AnalyzerLoan[];
  hasMore?: boolean;
}

interface AnalyzerLoansState {
  loans: AnalyzerLoan[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Search unfunded Active/Prospects files after a borrower/loan query is entered.
 *
 * Reads through `search_encompass_loans`, the same access-scoped RPC Loan
 * Center uses, so a Loan Officer sees only their assigned loans whenever the
 * own-loans scope applies. Pass `organizationId` only once Loan Center access
 * has resolved to a mode that may read loans.
 */
export function useAnalyzerLoans(
  organizationId: string | undefined,
  searchQuery: string
): AnalyzerLoansState {
  const requestIdRef = useRef(0);
  const [state, setState] = useState<AnalyzerLoansState>({
    loans: [],
    isLoading: false,
    error: null
  });

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const query = searchQuery.replace(/\s+/g, ' ').trim().slice(0, 100);
    if (!organizationId || query.length < ANALYZER_LOAN_SEARCH_MIN_LENGTH) {
      setState({ loans: [], isLoading: false, error: null });
      return;
    }

    setState({ loans: [], isLoading: true, error: null });
    const timer = window.setTimeout(async () => {
      try {
        const loans: AnalyzerLoan[] = [];
        const seen = new Set<string>();
        for (let page = 0; ; page += 1) {
          if (page >= 100) {
            throw new Error('Loan Center borrowers could not be loaded.');
          }
          const { data, error } = await supabase.rpc('search_encompass_loans', {
          p_organization_id: organizationId,
          p_search_query: query,
          // The RPC accepts one folder or all; keep its combined pagination
          // and access scope, then allow only Active/Prospects below.
          p_loan_folder: null,
          p_sort_field: 'creation_date',
          p_sort_direction: 'desc',
          p_limit: ANALYZER_LOAN_PAGE_SIZE,
          p_offset: page * ANALYZER_LOAN_PAGE_SIZE
        });
        if (requestId !== requestIdRef.current) {
          return;
        }
        if (error) {
          throw new Error(error.message);
        }
        const response: SearchResponse | null = typeof data === 'string'
          ? JSON.parse(data)
          : (data as SearchResponse | null);
          const rows = response?.loans ?? [];
          for (const loan of rows) {
            if (isAnalyzableLoan(loan) && !seen.has(loan.id)) {
              loans.push(loan);
              seen.add(loan.id);
            }
          }
          if (response?.hasMore !== true && rows.length < ANALYZER_LOAN_PAGE_SIZE) {
            break;
          }
          if (response?.hasMore === false) {
            break;
          }
        }
        setState({
          loans,
          isLoading: false,
          error: null
        });
      } catch (caught) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        const error = caught instanceof Error ? caught : new Error(String(caught));
        log.error('Failed to load loans for the bank statement analyzer', { metadata: { organizationId } }, error);
        setState({ loans: [], isLoading: false, error: error.message });
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      requestIdRef.current += 1;
    };
  }, [organizationId, searchQuery]);

  return state;
}
